import React from 'react';
import classNames from 'styles/utils/classNames';
import { formatPrice } from 'utils/shared/format';
import { SESSION_TONES } from 'components/MarketDataStatus/sessionTones';
import { SPOT_DOT_TITLE } from 'components/MarketDataStatus/marketDataStatusModel';
import type { MarketSessionPhase } from 'utils/shared/time/marketSession';
import { getChangedDigitIndices } from './getChangedDigitIndices';

type PriceDirection = 'up' | 'down';

interface PriceFlash {
  direction: PriceDirection;
  changedIndices: Set<number>;
}

interface Props {
  value: number;
  live?: boolean;
  formatter?: (value: number) => string;
  className?: string;
  /** Show the pulsing live dot. Digits still flash on change when false. Defaults to true. */
  showDot?: boolean;
  /** Diameter of the live indicator dot in px. Defaults to 6 (suits small text). */
  dotSize?: number;
  /** CSS `right` for the dot in px (negative pushes past the edge). Defaults to derived from size. */
  dotRight?: number;
  /** CSS `top` for the dot in px (negative lifts above the number). Defaults to derived from size. */
  dotTop?: number;
  /**
   * The running session, which colours the dot: green regular, blue pre-market, violet after-hours.
   *
   * Defaults to `regular` so existing call sites keep the green they have today. Passing the real
   * phase is what makes a price dot say the same thing as the status dot in the page header — they
   * read the same clock, so they cannot disagree.
   */
  sessionPhase?: MarketSessionPhase;
}

const FLASH_DURATION_MS = 900;

export default function LivePriceValue({
  value,
  live = false,
  formatter = formatPrice,
  className,
  showDot = true,
  dotSize = 6,
  dotRight,
  dotTop,
  sessionPhase = 'regular',
}: Props) {
  const formattedValue = React.useMemo(() => formatter(value), [formatter, value]);
  const previous = React.useRef<{ value: number; formattedValue: string } | null>(null);
  const clearFlashTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [flash, setFlash] = React.useState<PriceFlash | null>(null);

  React.useEffect(() => {
    const previousValue = previous.current;

    if (
      previousValue &&
      Number.isFinite(previousValue.value) &&
      Number.isFinite(value) &&
      previousValue.value !== value &&
      previousValue.formattedValue !== formattedValue
    ) {
      if (clearFlashTimer.current) clearTimeout(clearFlashTimer.current);

      setFlash({
        direction: value > previousValue.value ? 'up' : 'down',
        changedIndices: new Set(
          getChangedDigitIndices(previousValue.formattedValue, formattedValue),
        ),
      });

      clearFlashTimer.current = setTimeout(() => {
        clearFlashTimer.current = null;
        setFlash(null);
      }, FLASH_DURATION_MS);
    }

    previous.current = { value, formattedValue };
  }, [formattedValue, value]);

  React.useEffect(() => {
    return () => {
      if (clearFlashTimer.current) clearTimeout(clearFlashTimer.current);
    };
  }, []);

  const changedDigitColor =
    flash?.direction === 'up'
      ? 'text-bullish-green'
      : flash?.direction === 'down'
      ? 'text-graph-red'
      : '';

  // Tie the dot's offset to its size so a bigger dot hugs the number instead of
  // floating off in the corner. Call sites can override per placement.
  const dotOffsetRight = dotRight ?? -Math.round(dotSize * 0.5);
  const dotOffsetTop = dotTop ?? -Math.round(dotSize * 0.35);

  return (
    <span
      className={classNames('relative inline-flex items-baseline whitespace-nowrap', className)}
      title={live ? SPOT_DOT_TITLE[sessionPhase] : undefined}
    >
      {live && showDot && (
        <span
          className="pointer-events-none absolute inline-flex"
          style={{
            height: dotSize,
            width: dotSize,
            right: dotOffsetRight,
            top: dotOffsetTop,
          }}
          aria-hidden="true"
        >
          <span
            className={classNames(
              'absolute inline-flex h-full w-full animate-live-ping rounded-full motion-reduce:animate-none',
              SESSION_TONES[sessionPhase].ping,
            )}
          />
          <span
            className={classNames(
              'relative inline-flex h-full w-full rounded-full',
              SESSION_TONES[sessionPhase].dot,
            )}
          />
        </span>
      )}
      <span className="inline-flex items-baseline tabular-nums">
        {formattedValue.split('').map((char, index) => (
          <span
            key={index}
            className={classNames(
              'transition-colors duration-500',
              flash?.changedIndices.has(index) && changedDigitColor,
            )}
          >
            {char}
          </span>
        ))}
      </span>
    </span>
  );
}
